// LAP refonte Mes Objectifs — popup de création/ÉDITION d'objectif :
//   * objectif PRÉDÉFINI (catalogue, comportement historique) OU PERSONNALISÉ
//     (nom libre + unité libre) — bascule en tête de formulaire ;
//   * sélecteur de LIAISONS : rattacher l'objectif à des idées roadmap,
//     projets, listes, tâches ou événements (endpoint mesobjectifs-linkables) ;
//   * mode édition : prop `editObjectif` (carte → crayon) — PATCH + diff des
//     liaisons via POST /mesobjectifs/<id>/links/ {add, remove} ;
//   * en édition, champs statut + valeur atteinte (progression).
// Rétro-compatible avec les appels existants (UserWizard, SyntheseTemps,
// ouverture par URL) : les nouvelles props sont optionnelles.
import React, { useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import CustomButton from '../../../Components/CustomButton/CustomButton'
import ComboBox from '../../../Components/ComboBox'
import { makePatchRequest, makePostRequest } from '../../../utils/api'
import urls from '../../../utils/urls'
import { Box, Chip, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { convertToMuiDate, currentDate } from '../../../utils/helpers'
import { CustomTextField } from '../../../Components/CustomTextField'
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined'
import LinkIcon from '@mui/icons-material/Link'
import moment from 'moment'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

const LINK_FAMILIES = [
  { type: 'roadmap', key: 'linkTypeRoadmap' },
  { type: 'project', key: 'linkTypeProject' },
  { type: 'list', key: 'linkTypeList' },
  { type: 'task', key: 'linkTypeTask' },
  { type: 'event', key: 'linkTypeEvent' },
]

const LINKABLE_KEYS = {
  roadmap: 'roadmap',
  project: 'projects',
  list: 'lists',
  task: 'tasks',
  event: 'events',
}

const EMPTY_FORM = {
  domain: null,
  objectives_name: null,
  objectives_name_manual: '',
  unit_of_measurement: null,
  unit_of_measurement_manual: '',
  objectifs: '',
  reached: '',
  status: null,
  periodicity: null,
  date_objectives: currentDate(),
}

export default function CreateObjectifsForm({
  setObjectifs,
  objectifsMetadata,
  buttonIcon = false,
  fromUserWizard = false,
  selectedVal = '',
  setCreateObjif = false,
  setSelectedObjifList = null,
  openFromUrl = false,
  onUrlOpenConsumed = null,
  // LAP refonte — nouvelles props (toutes optionnelles)
  linkables = null,
  editObjectif = null,
  onEditConsumed = null,
  onSaved = null,
}) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState('predefined') // 'predefined' | 'custom'
  const [editId, setEditId] = useState(null)
  const [state, setState] = useState({
    predefinedObjectives: [],
    value_format: [],
  })
  const [formValues, setFormValues] = useState({ ...EMPTY_FORM })
  // Liaisons : sélection en cours + liste (pendantes en création, réelles en édition)
  const [linkFamily, setLinkFamily] = useState(null)
  const [linkItem, setLinkItem] = useState(null)
  const [links, setLinks] = useState([]) // [{target_type, target_id, label, id?}]
  const [removedLinkIds, setRemovedLinkIds] = useState([])

  useEffect(() => {
    if (fromUserWizard || openFromUrl) {
      handleClickOpen()
      if (openFromUrl && onUrlOpenConsumed) onUrlOpenConsumed()
    }
  }, [fromUserWizard, openFromUrl, onUrlOpenConsumed])

  // Mode ÉDITION : la page passe l'objectif de la carte cliquée.
  useEffect(() => {
    if (!editObjectif) return
    const isCustom = !!editObjectif.objectives_name_manual
    setMode(isCustom ? 'custom' : 'predefined')
    setEditId(editObjectif.id)
    const domain = (objectifsMetadata.domains || []).find(
      (d) => d.label === editObjectif.domain,
    )
    const periodicity = (objectifsMetadata.frequency || []).find(
      (f) => f.label === editObjectif.periodicity,
    )
    let predefined = null
    if (!isCustom) {
      predefined = (objectifsMetadata.predefinedObjectives || []).find(
        (p) => p.label === editObjectif.objectives_name,
      )
      if (domain) {
        setState({
          predefinedObjectives: (objectifsMetadata.predefinedObjectives || []).filter(
            (v) => v.domain === domain.id,
          ),
          value_format: predefined ? [{ label: predefined.value, id: predefined.id }] : [],
        })
      }
    }
    setFormValues({
      domain: domain || null,
      objectives_name: predefined || null,
      objectives_name_manual: editObjectif.objectives_name_manual || '',
      unit_of_measurement: predefined
        ? { label: editObjectif.unit_of_measurement, id: predefined.id }
        : null,
      unit_of_measurement_manual: editObjectif.unit_of_measurement || '',
      objectifs: editObjectif.objectifs_value ?? '',
      reached: editObjectif.reached ?? '',
      status: editObjectif.status ? { label: editObjectif.status } : null,
      periodicity: periodicity || null,
      date_objectives: editObjectif.date_objectives
        ? String(editObjectif.date_objectives).slice(0, 10)
        : currentDate(),
    })
    setLinks(
      (editObjectif.links || []).map((l) => ({
        id: l.id,
        target_type: l.target_type,
        target_id: l.target_id,
        label: l.label,
      })),
    )
    setRemovedLinkIds([])
    setOpen(true)
  }, [editObjectif])

  useEffect(() => {
    if (mode !== 'predefined') return
    const { domain } = formValues

    if (domain !== null && domain.length !== 0 && objectifsMetadata.predefinedObjectives) {
      state.predefinedObjectives = objectifsMetadata.predefinedObjectives.filter(
        (v) => v.domain === domain.id,
      )
      setState({ ...state })
    } else {
      state.predefinedObjectives = []
      setState({ ...state })
      formValues.objectives_name = null
      formValues.unit_of_measurement = null
      setFormValues({ ...formValues })
    }
  }, [formValues.domain])

  useEffect(() => {
    if (mode !== 'predefined') return
    const { objectives_name } = formValues
    if (state.predefinedObjectives.length !== 0 && objectives_name !== null) {
      state.value_format = objectifsMetadata.predefinedObjectives.filter(
        (v) => v.id === objectives_name.id,
      )
      state.value_format = state.value_format.map((v) => ({
        label: v.value,
        id: v.id,
      }))
      setState({ ...state })
    }
    if (formValues.objectives_name === null) {
      formValues.unit_of_measurement = null
      state.value_format = []
      setFormValues({ ...formValues })
      setState({ ...state })
    }
  }, [formValues.objectives_name])

  useEffect(() => {
    if (editId) return // pas de prefill « Temps de travail » en édition
    if (!open || !selectedVal || !objectifsMetadata.predefinedObjectives?.length) return
    if (formValues.objectives_name?.label === selectedVal) return

    const selectedObjective = objectifsMetadata.predefinedObjectives.find(
      (e) => e.label === selectedVal,
    )
    if (!selectedObjective) return

    const valueFormats = objectifsMetadata.predefinedObjectives
      .filter((v) => v.id === selectedObjective.id)
      .map((v) => ({ label: v.value, id: v.id }))

    setState({
      predefinedObjectives: objectifsMetadata.predefinedObjectives.filter(
        (v) => v.domain === selectedObjective.domain,
      ),
      value_format: valueFormats,
    })
    setFormValues({
      ...formValues,
      domain: objectifsMetadata.domains?.find((e) => e.id === selectedObjective.domain) || null,
      objectives_name: selectedObjective,
      unit_of_measurement: valueFormats.find((e) => e.label === selectedObjective.value) || null,
      periodicity: objectifsMetadata.frequency?.find((e) => e.label === 'Mois') || null,
      date_objectives: convertToMuiDate(moment().add(1, 'year').format('DD-MM-YYYY')),
    })
  }, [open, selectedVal, objectifsMetadata, formValues, editId])

  const handleClickOpen = () => {
    setOpen(true)
    if (
      selectedVal !== null &&
      selectedVal.length !== 0 &&
      objectifsMetadata.predefinedObjectives
    ) {
      let domain = objectifsMetadata.predefinedObjectives.find((e) => e.label === selectedVal)
      if (domain) {
        state.predefinedObjectives = objectifsMetadata.predefinedObjectives.filter(
          (v) => v.domain === domain.id,
        )
        state.value_format = objectifsMetadata.predefinedObjectives.filter(
          (v) => v.id === domain.id,
        )
        state.value_format = state.value_format.map((v) => ({
          label: v.value,
          id: v.id,
        }))
        setState({ ...state })
        formValues.domain = objectifsMetadata.domains.find((e) => e.id === domain.domain)
        formValues.objectives_name = domain
        formValues.unit_of_measurement = state.value_format.find((e) => e.label === domain.value)
        formValues.periodicity = objectifsMetadata.frequency.find((e) => e.label === 'Mois')
        formValues.date_objectives = convertToMuiDate(moment().add(1, 'year').format('DD-MM-YYYY'))
        setFormValues({ ...formValues })
      }
    }
  }

  const handleClose = () => {
    setOpen(false)
    setMode('predefined')
    setEditId(null)
    setFormValues({ ...EMPTY_FORM })
    setLinks([])
    setRemovedLinkIds([])
    setLinkFamily(null)
    setLinkItem(null)
    setState({ predefinedObjectives: [], value_format: [] })
    if (fromUserWizard) {
      setCreateObjif(false)
    }
    if (onEditConsumed) onEditConsumed()
  }

  const handleChange = (i, j) => {
    setFormValues({ ...formValues, [i]: j })
  }

  // --- Liaisons -------------------------------------------------------------
  const linkableOptions = () => {
    if (!linkables || !linkFamily) return []
    const familyKey = LINKABLE_KEYS[linkFamily.type]
    const alreadyLinked = new Set(
      links.map((l) => `${l.target_type}:${l.target_id}`),
    )
    return (linkables[familyKey] || [])
      .filter((item) => !alreadyLinked.has(`${linkFamily.type}:${String(item.id)}`))
      .map((item) => ({ label: item.label, id: item.id }))
  }

  const addLink = () => {
    if (!linkFamily || !linkItem) return
    setLinks([
      ...links,
      { target_type: linkFamily.type, target_id: String(linkItem.id), label: linkItem.label },
    ])
    setLinkItem(null)
  }

  const removeLink = (index) => {
    const link = links[index]
    if (link.id) setRemovedLinkIds([...removedLinkIds, link.id])
    setLinks(links.filter((_, i) => i !== index))
  }

  // --- Soumission -----------------------------------------------------------
  const onSubmit = async (e) => {
    e.preventDefault()
    const isCustom = mode === 'custom'
    const payload = {
      domain: formValues.domain?.id,
      periodicity: formValues.periodicity?.id,
      objectifs: formValues.objectifs,
      date_objectives: formValues.date_objectives,
      unit_of_measurement: isCustom
        ? formValues.unit_of_measurement_manual
        : formValues.unit_of_measurement?.label,
      ...(isCustom
        ? { objectives_name_manual: formValues.objectives_name_manual }
        : { objectives_name: formValues.objectives_name?.id }),
    }

    if (fromUserWizard) {
      setSelectedObjifList((prev) => [...prev, formValues])
      handleClose()
      return
    }

    if (editId) {
      // ÉDITION : PATCH des champs + diff des liaisons.
      if (formValues.reached !== '') payload.reached = formValues.reached
      if (formValues.status?.label) payload.status = formValues.status.label
      const response = await makePatchRequest(
        `${urls.crudMesObjectifs}${editId}/`,
        payload,
        {},
        true,
      )
      if (response.status === 200) {
        const added = links.filter((l) => !l.id)
        if (added.length || removedLinkIds.length) {
          await makePostRequest(
            urls.mesObjectifsLinks(editId),
            {
              add: added.map((l) => ({ target_type: l.target_type, target_id: l.target_id })),
              remove: removedLinkIds,
            },
            {},
            true,
          )
        }
        if (onSaved) onSaved()
        handleClose()
      }
      return
    }

    // CRÉATION : les liaisons partent dans le même POST (backend perform_create).
    payload.links = links.map((l) => ({ target_type: l.target_type, target_id: l.target_id }))
    const response = await makePostRequest(urls.crudMesObjectifs, payload, {}, true)
    if (response.status === 201) {
      if (onSaved) {
        onSaved()
      } else if (setObjectifs) {
        setObjectifs((objectifs) => ({
          ...objectifs,
          results: [...(objectifs.results || []), response.data],
          count: (objectifs.count || 0) + 1,
        }))
      }
      handleClose()
    }
  }

  return (
    <>
      {fromUserWizard ? (
        ''
      ) : (
        <>
          {buttonIcon ? (
            <IconButton sx={{ color: 'white' }} onClick={handleClickOpen}>
              <ControlPointOutlinedIcon />
            </IconButton>
          ) : (
            <CustomButton onClick={handleClickOpen}>{t('mesObjectifs.add')}</CustomButton>
          )}
        </>
      )}
      <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose}>
        <DialogTitle sx={{ color: 'black !important' }}>
          {editId ? t('mesObjectifs.editDialogTitle') : t('mesObjectifs.createDialogTitle')}
        </DialogTitle>
        <form onSubmit={onSubmit}>
          <DialogContent>
            {/* Bascule prédéfini / personnalisé */}
            <ToggleButtonGroup
              value={mode}
              exclusive
              size="small"
              fullWidth
              onChange={(e, newMode) => {
                if (newMode) setMode(newMode)
              }}
              sx={{ mb: 1 }}
            >
              <ToggleButton value="predefined">{t('mesObjectifs.modePredefined')}</ToggleButton>
              <ToggleButton value="custom">{t('mesObjectifs.modeCustom')}</ToggleButton>
            </ToggleButtonGroup>

            <ComboBox
              handleAutoComplete={(i, j) => {
                if (selectedVal) {
                  formValues.objectives_name = null
                  formValues.unit_of_measurement = null
                  setFormValues({ ...formValues })
                }
                handleChange(i, j)
              }}
              label={t('mesObjectifs.domainLabel')}
              data={objectifsMetadata.domains || []}
              name="domain"
              required
              selectedValue={formValues.domain}
            />

            {mode === 'predefined' ? (
              <>
                <ComboBox
                  handleAutoComplete={handleChange}
                  label={t('mesObjectifs.objectivesLabel')}
                  data={state.predefinedObjectives || []}
                  name="objectives_name"
                  required
                  selectedValue={formValues.objectives_name}
                />
                <ComboBox
                  handleAutoComplete={handleChange}
                  label={t('mesObjectifs.unitOfMeasureLabel')}
                  data={state.value_format || []}
                  name="unit_of_measurement"
                  required
                  selectedValue={formValues.unit_of_measurement}
                />
              </>
            ) : (
              <>
                {/* Objectif PERSONNALISÉ : nom libre + unité libre */}
                <CustomTextField
                  name="objectives_name_manual"
                  onChange={(e) =>
                    setFormValues({ ...formValues, objectives_name_manual: e.target.value })
                  }
                  label={t('mesObjectifs.customNameLabel')}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                  value={formValues.objectives_name_manual}
                />
                <CustomTextField
                  name="unit_of_measurement_manual"
                  onChange={(e) =>
                    setFormValues({ ...formValues, unit_of_measurement_manual: e.target.value })
                  }
                  label={t('mesObjectifs.customUnitLabel')}
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={formValues.unit_of_measurement_manual}
                />
              </>
            )}

            <CustomTextField
              name="objectifs"
              onChange={(e) => setFormValues({ ...formValues, objectifs: e.target.value })}
              label={t('mesObjectifs.valueLabel')}
              variant="outlined"
              type="number"
              required
              fullWidth
              size="small"
              value={formValues.objectifs}
            />
            {editId && (
              <>
                {/* Édition : progression + statut */}
                <CustomTextField
                  name="reached"
                  onChange={(e) => setFormValues({ ...formValues, reached: e.target.value })}
                  label={t('mesObjectifs.reachedLabel')}
                  variant="outlined"
                  type="number"
                  fullWidth
                  size="small"
                  value={formValues.reached}
                />
                <ComboBox
                  handleAutoComplete={handleChange}
                  label={t('mesObjectifs.colStatus')}
                  data={[
                    { label: 'Engagé' },
                    { label: 'En cours' },
                    { label: 'reussite' },
                    { label: 'Annulé' },
                  ]}
                  name="status"
                  selectedValue={formValues.status}
                />
              </>
            )}
            <CustomTextField
              name="date_objectives"
              label={t('mesObjectifs.dateOfObjectiveLabel')}
              variant="outlined"
              type="date"
              required
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  date_objectives: e.target.value,
                })
              }
              fullWidth
              size="small"
              value={formValues.date_objectives}
            />
            <ComboBox
              handleAutoComplete={handleChange}
              label={t('mesObjectifs.periodicityLabel')}
              data={objectifsMetadata.frequency || []}
              name="periodicity"
              required
              selectedValue={formValues.periodicity}
            />

            {/* Sélecteur de liaisons (roadmap / projets / listes / tâches / événements) */}
            {linkables && (
              <Box mt={2}>
                <Box sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
                  {t('mesObjectifs.linksSectionTitle')}
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <ComboBox
                    handleAutoComplete={(i, j) => {
                      setLinkFamily(j)
                      setLinkItem(null)
                    }}
                    label={t('mesObjectifs.linkTypeLabel')}
                    data={LINK_FAMILIES.map((f) => ({
                      label: t(`mesObjectifs.${f.key}`),
                      id: f.type,
                      type: f.type,
                    }))}
                    name="link_family"
                    selectedValue={linkFamily}
                    sx={{ minWidth: '130px', flex: 1 }}
                  />
                  <ComboBox
                    handleAutoComplete={(i, j) => setLinkItem(j)}
                    label={t('mesObjectifs.linkItemLabel')}
                    data={linkableOptions()}
                    name="link_item"
                    selectedValue={linkItem}
                    sx={{ minWidth: '150px', flex: 2 }}
                  />
                  <IconButton
                    onClick={addLink}
                    disabled={!linkFamily || !linkItem}
                    sx={{ mt: '10px' }}
                  >
                    <ControlPointOutlinedIcon />
                  </IconButton>
                </Box>
                {links.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                    {links.map((link, index) => (
                      <Chip
                        key={`${link.target_type}-${link.target_id}`}
                        size="small"
                        icon={<LinkIcon sx={{ fontSize: 14 }} />}
                        label={link.label}
                        onDelete={() => removeLink(index)}
                        sx={{ maxWidth: 220 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button type="submit">{t('mesObjectifs.validate')}</Button>
            <Button onClick={handleClose}>{t('mesObjectifs.cancel')}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
